/**
 * MIT License
 *
 * Copyright (c) 2023 Jamasp Team <mailto:radmanesh@gmail.com Arman Radmanesh> <mailto:salarirad@gmail.com Salar Rad>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { useEffect } from "react";
import { Navigate, Outlet, Route, BrowserRouter as Router, Routes, useNavigate } from "react-router-dom";
import AdminDashboard from "./admin/pages/AdminDashboard";
import NewProject from "./admin/pages/NewProject";
import ShowParticipant from "./admin/pages/ShowParticipant";
import ShowProject from "./admin/pages/ShowProject";
import Users from "./admin/pages/Users";
import { AuthProvider } from './auth/AuthContext';
import Login from "./auth/Login";
import { RegistrationProvider } from "./context/RegistrationContext";
import { auth } from "./firebase";
import AuthLayout from "./layout/AuthLayout";
import BlankLayout from "./layout/BlankLayout";
import ProtectedLayout from "./layout/ProtectedLayout";
import About from "./pages/About";
import Explore from "./pages/Explore";
import Dashboard from "./pages/Dashboard";
import FitbitCallback from "./pages/FitbitCallback";
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import Privacy from "./pages/Privacy";
import ProlificRegistration from "./pages/ProlificRegistration";
import Terms from "./pages/Terms";
import Test from "./pages/Test";
import UserProfile from "./pages/UserProfile";
import BareLayout from "./layout/BareLayout";
import ProjectParticipants from "./admin/pages/ProjectParticipants";
import EmailRegistration from "./pages/EmailRegistration";
import EmailRegistrationDirect from "./pages/EmailRegistrationDirect";
import GoatCounterTracker from "./analytics/GoatCounterTracker";

/**
 * Logout component that handles user sign-out and redirects to the home page.
 *
 * This component uses the `useEffect` hook to sign out the user when the component is mounted.
 * After signing out, it navigates the user to the home page ("/").
 *
 * @component
 * @example
 * return (
 *   <Logout />
 * )
 */
const Logout = () => {
  const navigate = useNavigate();
  useEffect(() => {
    auth.signOut().then(() => navigate("/")); // Or any redirect action after signout
  }, [navigate]);
  return <div>Logging out...</div>;
};


/**
 * Main application component that sets up routing and authentication.
 *
 * @component
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * return (
 *   <App />
 * )
 *
 * @description
 * This component uses `AuthProvider` to manage authentication state and `Router` to handle routing.
 * It defines routes for different user roles (user, admin) and public routes.
 *
 * Routes:
 * - User routes: `/user`, `/user/home`, `/user/profile`
 * - Admin routes: `/admin`, `/admin/users`, `/admin/users/:userId`, `/admin/newProject`, `/admin/showProject/:projectId`
 * - Auth routes: `/auth/login`, `/auth/phoneLogin`, `/auth/phone-verify`, `/auth/logout`
 * - Public routes: `/`, `/home`, `/about`, `/terms`, `/privacy`, `reg/prolificRegistration/:projectId/pId/:pId?`, `/test`, `/notFound`
 */
export default function App() {

  return (
    <div className="app">
      <Router future={{ v7_startTransition: true }}>
        <GoatCounterTracker />
        <AuthProvider>
          <Routes>
            {/* User routes */}
            <Route path="/user" element={<ProtectedLayout />}>
              <Route path="" element={<Navigate to="/user/home" replace />} />
              <Route path="home" element={<Dashboard />} />
              <Route path="profile" element={<UserProfile />} />
            </Route>

            {/* Admin routes */}
            <Route path="/admin" element={<ProtectedLayout role="admin" />}>
              <Route path="" element={<AdminDashboard />} />
              <Route path="users" element={<Users />} />
              {/* <Route path="users/:userId" element={<ShowUser />} /> */}
              <Route path="participants/:projectId" element={<ProjectParticipants />} />
              <Route path="participants/:participantId" element={<ShowParticipant />} />
              <Route path="participants/:participantId/project?/:projectId?" element={<ShowParticipant />} />
              <Route path="newProject" element={<NewProject />} />
              <Route path="showProject/:projectId" element={<ShowProject />} />
            </Route>

            {/* Auth routes */}
            <Route path="/auth" element={<AuthLayout />}>
              <Route path="login" element={<Login />} />
              {/* <Route path="phoneLogin" element={<PhoneLogin />} />
              <Route path="phone-verify" element={<PhoneVerify />} /> */}
              <Route path="logout" element={<Logout />} /> {/* Updated logout handling */}
            </Route>

            {/* Public routes */}
            <Route path="/" element={<BlankLayout />}>
              <Route path="" element={<Navigate to="/home" replace />} />
              <Route path="home" element={<Home />} />
              <Route path="explore" element={<Explore />} />
              <Route path="about" element={<About />} />
              <Route path="terms" element={<Terms />} />
              <Route path="privacy" element={<Privacy />} />
              <Route path="test" element={<Test />} />
              <Route path="notFound" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/notFound" replace />} />
            </Route>

            {/* Bare Layout for registration */}
            <Route path="/reg" element={<BareLayout />}>
              <Route element={<RegistrationProvider> <Outlet /> </RegistrationProvider>}>
                <Route path="prolificRegistration/:projectId" element={<ProlificRegistration />} />
                <Route path="prolificRegistration/:projectId/pId?/:pId?" element={<ProlificRegistration />} />
                <Route path="emailRegistration/:projectId?/code?/:code?" element={<EmailRegistration />} />
                <Route path="emailRegistrationDirect/:projectId?/code?/:code?" element={<EmailRegistrationDirect />} />
                <Route path="health_callback" element={<FitbitCallback />} />
              </Route>
            </Route>

          </Routes>
        </AuthProvider>
      </Router>
    </div>
  );
}
